import React from 'react';
import ReactDOM from 'react-dom/client';
import * as Sentry from '@sentry/react';
import App from './App';
import './index.css';
import { initOfflineDetection } from './utils/offlineDetection';
import { ErrorBoundary } from './components/ErrorBoundary';
import { flushReferencesQueue } from './services/googleDrive/referencesWriteQueue';
import { flushDestinoSync } from './services/destinoDriveSync';
import { flushAllPendingSumarioSaves } from './components/SumarioComentado/DriveSync';
import { flushPreferencesQueue } from './stores/preferencesStore';

// Capture beforeinstallprompt as early as possible (before components mount)
window.addEventListener('beforeinstallprompt', (e) => {
  e.preventDefault();
  (window as unknown as Record<string, unknown>).__installPrompt = e;
});

// Initialize Sentry error monitoring
const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
if (sentryDsn) {
  const hostname = window.location.hostname;
  const environment = hostname === 'bigwa.app'
    ? 'production'
    : hostname.includes('staging')
      ? 'staging'
      : 'development';

  Sentry.init({
    dsn: sentryDsn,
    environment,
    sampleRate: 1.0,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({ maskAllText: true, blockAllMedia: true }),
    ],
    tracesSampleRate: 0.2,
    replaysOnErrorSampleRate: 1.0,
    replaysSessionSampleRate: 0,
  });
}

// Flush pending debounced saves before the tab closes (eager imports — A7)
window.addEventListener('beforeunload', () => {
  flushReferencesQueue();
  flushDestinoSync();
  flushAllPendingSumarioSaves();
  flushPreferencesQueue();
});

// Global handler for unhandled promise rejections
window.addEventListener('unhandledrejection', (event) => {
  const reason = event.reason;
  const message = reason?.message || String(reason);
  // Import logger dynamically to avoid circular deps at boot
  import('./utils/logger').then(m => m.logger.error('UnhandledRejection', message, reason)).catch(() => {});
});

// Initialize offline queue IndexedDB (best-effort — não bloqueia)
import('./services/offlineQueue').then(({ initOfflineQueue }) => {
  initOfflineQueue().catch(() => {}); // IDB indisponível é OK — degradação graciosa
});

// Initialize offline detection
initOfflineDetection();

ReactDOM.createRoot(document.getElementById('root')!).render(
  <ErrorBoundary section="App" fallback={
    <div style={{ padding: '48px', textAlign: 'center', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      <p style={{ fontSize: '16px', color: '#333' }}>O Bigwa encontrou um erro inesperado.</p>
      <button
        onClick={() => window.location.reload()}
        style={{
          marginTop: '16px',
          background: '#477B72',
          color: '#fff',
          border: 'none',
          borderRadius: '8px',
          padding: '12px 32px',
          fontSize: '14px',
          fontWeight: 500,
          cursor: 'pointer',
        }}
      >
        Recarregar página
      </button>
    </div>
  }>
    <App />
  </ErrorBoundary>
);
